import { memo } from 'react';
import { LAYOUT } from './layout';
import { SLD_COLORS } from '../utils/sldTheme';
import type { SLDDeviceState } from '../utils/sldTypes';
import type { DeviceAlarmSeverity } from '../utils/deviceAlarmSeverity';

/**
 * Suffixes appended to the running status label when a generator is producing
 * while carrying an active alarm. Shared by the rendered tspan and the
 * load-% x-offset calculation so the two cannot drift. djin-cloud#622, #684.
 */
const FAULT_SUFFIX = ' · FAULT';
const WARNING_SUFFIX = ' · WARNING';
const DEGRADED_SUFFIX = ' · DEGRADED';

interface GeneratorCardProps {
  x: number;
  y: number;         // center Y of the row
  label: string;
  activePowerKw: number | null;
  engineLoadPct: number | null;
  status: string;
  faulted: boolean;
  commsOk?: boolean;       // default true for back-compat
  breakerClosed?: boolean; // output signal; when absent, falls back to power > 0
  deviceState?: SLDDeviceState;
  /**
   * Highest ACTIVE (uncleared AND unacknowledged) alarm severity for this
   * device, or null when nothing is active. The ONLY input that raises the
   * card's fault/warning badge — the telemetry `faulted` flag deliberately
   * does not, because it stays true for acked-and-open alarms. See
   * djin-cloud#684 and utils/deviceAlarmSeverity.ts.
   */
  alarmSeverity?: DeviceAlarmSeverity | null;
  onClick: () => void;
}

function GeneratorCardInner({
  x, y, label, activePowerKw, engineLoadPct, status, faulted, commsOk, breakerClosed, deviceState,
  alarmSeverity, onClick,
}: GeneratorCardProps) {
  const w = LAYOUT.cardW;
  const h = LAYOUT.cardH;
  const cardY = y - h / 2;

  // Back-compat path: if deviceState wasn't provided, fall back to the
  // pre-#96 boolean-driven derivation. New callers always provide it.
  // VerticalSLD.tsx always supplies deviceState; this branch survives for
  // defensive back-compat only (#96 Q5 — out of scope).
  const rawStatus = (status ?? '').toLowerCase();
  // Strict deviceState check — running-with-fault is handled by the overlay
  // below so the operator sees what the unit is doing while still being warned
  // about the alarm. Mirrors BESSCard.tsx:32-34. The `faulted || rawStatus`
  // disjunction is retained ONLY for the legacy no-deviceState path documented
  // above; letting it override a supplied tier is what re-blanked the kW of a
  // running generator in djin-cloud#622.
  const isFault = deviceState != null
    ? deviceState === 'fault'
    : (faulted || rawStatus === 'fault');
  const isOffline = deviceState === 'offline';
  const isNoComms = deviceState === 'no_comms';
  const isUnknown = deviceState === 'unknown';
  const isLegacyDisconnected = !deviceState && commsOk === false;
  const isStandby = deviceState === 'standby';
  // 'degraded' is a RUNNING tier (djin-cloud#618) and must be folded into
  // `isOn` here, not handled beside it. Every downstream branch in this
  // component — body fill, status dot, `suppressNumerics`, the status line —
  // is written as "isOn or else", so a running-but-impaired generator omitted
  // from `isOn` would fall through to the Standby/Offline arms and re-blank
  // the kW of a producing unit: exactly the regression #622 fixed.
  const isDegraded = deviceState === 'degraded';
  const isOn = deviceState === 'running' || isDegraded ||
    (!deviceState && !isLegacyDisconnected && (rawStatus === 'on' || rawStatus === 'running') &&
      ((activePowerKw ?? 0) > 0 || breakerClosed === true));

  // Body palette
  let bodyFill: string = SLD_COLORS.generatorOff.fill;
  let bodyStroke: string = SLD_COLORS.generatorOff.stroke;
  if (isFault) {
    bodyFill = SLD_COLORS.generatorFault.fill;
    bodyStroke = SLD_COLORS.generatorFault.stroke;
  } else if (isNoComms) {
    bodyFill = SLD_COLORS.stale.fill;
    bodyStroke = SLD_COLORS.stale.stroke;
  } else if (isOffline || isLegacyDisconnected || isUnknown) {
    bodyFill = '#1e1e2e';
    bodyStroke = '#64748b';
  } else if (isOn) {
    bodyFill = SLD_COLORS.generatorOn.fill;
    bodyStroke = SLD_COLORS.generatorOn.stroke;
  }

  // Status dot
  const dotColor = isFault || isOffline ? '#fb7185'
    : isNoComms ? '#fbbf24'
    : isUnknown ? '#475569'
    : isLegacyDisconnected ? '#94a3b8'
    : isOn ? '#34d399'
    : '#fbbf24';

  // Numerics suppressed for any tier where the displayed numbers cannot be trusted.
  const suppressNumerics = isFault || isOffline || isNoComms || isLegacyDisconnected || isUnknown;

  // Active-alarm overlay: keep the operating body + label but suffix
  // " · FAULT" (critical, pulsing badge) or " · WARNING" (warning, static amber
  // badge), so an alarmed-but-producing generator is distinguishable from both a
  // healthy one and a dead one. Driven by ACTIVE alarms only — never by the
  // telemetry fault flag, which stays true for acked-and-open rows.
  // djin-cloud#622, #684.
  const overlaySeverity =
    !isFault && !isOffline && !isNoComms && !isLegacyDisconnected && !isUnknown
      ? alarmSeverity ?? null
      : null;
  const showFaultOverlay = overlaySeverity === 'critical';
  const showWarningOverlay = overlaySeverity === 'warning';
  // ONE overlay at a time, in decreasing specificity: FAULT (an active critical
  // alarm) > WARNING (an active warning alarm) > DEGRADED (the edge's own
  // running-but-impaired status, djin-cloud#618). DEGRADED is last because it is
  // the least specific of the three — it says something is wrong somewhere in
  // the block without naming it — and it is the only one that fires with no
  // cloud alarm row at all (a sibling device offline raises it), which is
  // exactly the case #618 exists to surface.
  const showDegradedOverlay = isDegraded && !showFaultOverlay && !showWarningOverlay;
  const power = suppressNumerics
    ? null
    : (activePowerKw != null ? Math.round(activePowerKw) : null);
  const powerStr = power != null ? power.toLocaleString() : (suppressNumerics ? '--' : '0');
  const loadStr = engineLoadPct != null ? `${Math.round(engineLoadPct)}%` : null;

  // Status line — priority Fault > Offline > No Comms > Disconnected (legacy) > Running > Standby
  let statusText = 'Offline';
  let statusColor = '#64748b';
  if (isFault) {
    statusText = 'FAULT';
    statusColor = '#fb7185';
  } else if (isOffline) {
    statusText = 'OFFLINE';
    statusColor = '#fb7185';
  } else if (isNoComms) {
    statusText = 'NO COMMS';
    statusColor = '#fbbf24';
  } else if (isLegacyDisconnected) {
    statusText = 'Disconnected';
    statusColor = '#94a3b8';
  } else if (isUnknown) {
    statusText = 'awaiting data';
    statusColor = '#64748b';
  } else if (isOn) {
    statusText = 'Running';
    statusColor = '#94a3b8';
  } else if (isStandby || rawStatus === 'standby' || rawStatus === '' || rawStatus === 'off') {
    statusText = 'Standby';
    statusColor = '#64748b';
  }

  // The status line renders `statusText` and, when overlaid, the FAULT_SUFFIX
  // tspan inside the SAME <text>. The load % is a separate <text> x-positioned
  // from the label width, so it must clear the full rendered label — measuring
  // `statusText` alone drops "64%" on top of the suffix. Declared after the
  // status-line block above because `statusText` is assigned there.
  const statusLabelChars = statusText.length
    + (showFaultOverlay ? FAULT_SUFFIX.length : 0)
    + (showWarningOverlay ? WARNING_SUFFIX.length : 0)
    + (showDegradedOverlay ? DEGRADED_SUFFIX.length : 0);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect
        x={x} y={cardY} width={w} height={h} rx={LAYOUT.cardRx}
        fill={bodyFill} stroke={bodyStroke} strokeWidth={1.2} opacity={0.95}
      />
      <text x={x + 16} y={cardY + 26} fill="#e2e8f0" fontSize="14" fontWeight="700" fontFamily="monospace">
        {label}
      </text>
      <circle cx={x + w - 20} cy={cardY + 22} r={5} fill={dotColor} />
      {isFault && (
        <circle cx={x + w - 20} cy={cardY + 22} r={5} fill={dotColor} className="sld-pulse" />
      )}
      <text x={x + 16} y={cardY + 52} fill="#ffffff" fontSize="18" fontWeight="700" fontFamily="monospace">
        {powerStr}
      </text>
      <text x={x + 16 + (powerStr.length * 11) + 4} y={cardY + 52} fill="#94a3b8" fontSize="13" fontWeight="500">
        kW
      </text>
      <text x={x + 16} y={cardY + 74} fill={statusColor} fontSize="12" fontWeight={isFault || isOffline || isNoComms ? 700 : 400}>
        {statusText}
        {showFaultOverlay && (
          <tspan fill="#fb7185" fontWeight={700}>{FAULT_SUFFIX}</tspan>
        )}
        {showWarningOverlay && (
          <tspan fill="#fbbf24" fontWeight={700}>{WARNING_SUFFIX}</tspan>
        )}
        {showDegradedOverlay && (
          <tspan fill="#fbbf24" fontWeight={700}>{DEGRADED_SUFFIX}</tspan>
        )}
      </text>
      {isOn && loadStr && (
        <text x={x + 16 + statusLabelChars * 7 + 6} y={cardY + 74} fill={dotColor} fontSize="12" fontWeight="700">
          {loadStr}
        </text>
      )}
      {showFaultOverlay && (
        <circle
          cx={x + w - 36}
          cy={cardY + 22}
          r={5}
          fill="#fb7185"
          className="sld-pulse"
          data-testid="generator-fault-overlay-badge"
        />
      )}
      {/* Amber badge: same corner, deliberately NOT pulsing — the pulse is
          reserved for critical so the two read differently at a glance.
          WARNING and DEGRADED share the slot; the label distinguishes them. */}
      {showWarningOverlay && (
        <circle
          cx={x + w - 36}
          cy={cardY + 22}
          r={5}
          fill="#fbbf24"
          data-testid="generator-warning-overlay-badge"
        />
      )}
      {showDegradedOverlay && (
        <circle
          cx={x + w - 36}
          cy={cardY + 22}
          r={5}
          fill="#fbbf24"
          data-testid="generator-degraded-overlay-badge"
        />
      )}
    </g>
  );
}

export const GeneratorCard = memo(GeneratorCardInner);
